"use client"

import React, { useRef, useState, useEffect } from 'react';

import { useAuth } from '@clerk/nextjs'

import useProjectStore from "@/store/projectStore";
import useUserStore from "@/store/userStore";
import useStore from "@/store/store";
import VoiceSelectModal from "@/components/Modals/VoiceSelectModal";

import { getPlanBySlug } from "@/app/enum-subscription";

import { Speech, ChevronRight, Trash, AudioLines, FolderUp, Mic } from 'lucide-react';


const Step2Audio = ({ onHandleInputChange }) => {
    const [activeTab, setActiveTab] = useState("upload"); // "tts" | "upload" | "record"

    const { userPlan } = useUserStore()
    const currentPlan = getPlanBySlug(userPlan);
    const { openPricingModal } = useStore()

    const { projAudio, setProjAudio } = useProjectStore()

    /**
     * 获取用户允许的最大音频上传大小（以字节为单位）
     * 层级递进检查：pro > premium > base/free
     * @returns {number|null} 返回最大字节数，如果是 null 表示无限制
     */
    const getMaxAudioSizeLimit = () => {
        if (currentPlan.features.unlimited_upload) {
            return null; // null 表示无限制
        }

        return currentPlan.features.audio_upload_limit * 1024 * 1024;
    }

    /**
     * 统一的音频处理函数
     * 验证音频文件，创建预览，更新 store 和 formData
     * @param {File} audioFile - 音频文件对象
     * @param {string} sourceType - 音频来源类型: "upload" | "tts" | "record"
     */
    const handleAudioReady = (audioFile, sourceType) => {
        if (!audioFile) return;

        // 验证文件格式
        const validTypes = ['audio/mp3', 'audio/wav', 'audio/mpeg', 'audio/webm'];
        if (!validTypes.includes(audioFile.type)) {
            alert('Audio format must be MP3, WAV, or WebM');
            return;
        }

        // 检查音频文件大小权限
        const maxAudioSize = getMaxAudioSizeLimit();
        if (maxAudioSize !== null && audioFile.size > maxAudioSize) {
            const maxSizeMB = (maxAudioSize / (1024 * 1024)).toFixed(0);
            openPricingModal({
                title: "Audio Too Large",
                description: `Your current plan limits audio uploads to ${maxSizeMB}MB. Upgrade your subscription to upload larger audio files and create longer lip-sync videos.`
            });
            return;
        }

        // 释放之前的 Blob URL，避免内存泄漏
        if (projAudio.source && projAudio.source.startsWith('blob:')) {
            URL.revokeObjectURL(projAudio.source);
        }

        // 创建新的 Blob URL 用于预览
        const audioURL = URL.createObjectURL(audioFile);

        // 创建临时 audio 元素来获取时长
        const audioElement = document.createElement('audio');
        audioElement.src = audioURL;

        const cleanup = () => {
            audioElement.removeAttribute('src');
            audioElement.load();
        };

        audioElement.onloadedmetadata = () => {
            const duration = parseFloat(audioElement.duration.toFixed(2));

            // 验证时长
            if (duration > 300) {
                alert('Audio duration must be less than 5 min');
                URL.revokeObjectURL(audioURL);
                cleanup();
                return;
            }

            // 更新 formData
            onHandleInputChange("audio", audioFile);

            // 更新 zustand store
            setProjAudio({
                type: sourceType,
                source: audioURL,
                name: audioFile.name,
                duration: duration,
                file: audioFile
            });

            cleanup();
        };

        audioElement.onerror = () => {
            alert('Failed to load audio file');
            URL.revokeObjectURL(audioURL);
            cleanup();
        };
    };

    /**
     * 重置音频
     */
    const resetAudio = () => {
        // 释放 Blob URL
        if (projAudio.source && projAudio.source.startsWith('blob:')) {
            URL.revokeObjectURL(projAudio.source);
        }

        setProjAudio({
            type: "upload",
            source: null,
            name: "",
            duration: 0,
            file: null
        });
    };

    return (
        <div className="card-property mb-24">
            {/* Tabs 导航 */}
            <div role="tablist" className="tabs tabs-box mb-4 w-full">
                <div
                    role="tab"
                    className={`tab gap-2 flex-1 px-0 md:px-4 ${activeTab === "tts" ? "tab-active" : ""}`}
                    onClick={() => setActiveTab("tts")}
                >
                    <AudioLines className="hidden md:block" size={16} />
                    <span>Text to Speech</span>
                </div>
                <div
                    role="tab"
                    className={`tab gap-2 flex-1 px-0 md:px-4 ${activeTab === "upload" ? "tab-active" : ""}`}
                    onClick={() => setActiveTab("upload")}
                >
                    <FolderUp className="hidden md:block" size={16} />
                    <span>Upload Audio</span>
                </div>
                <div
                    role="tab"
                    className={`tab gap-2 flex-1 px-0 md:px-4 ${activeTab === "record" ? "tab-active" : ""}`}
                    onClick={() => setActiveTab("record")}
                >
                    <Mic className="hidden md:block" size={16} />
                    <span>Record Audio</span>
                </div>
            </div>

            {/* Tab 内容区域 */}
            <div className="mb-4">
                {activeTab === "tts" && (
                    <TabInputText
                        onAudioReady={(audioFile) => handleAudioReady(audioFile, "tts")}
                    />
                )}
                {activeTab === "upload" && (
                    <TabUploadAudio
                        onAudioReady={(audioFile) => handleAudioReady(audioFile, "upload")}
                        getMaxAudioSizeLimit={getMaxAudioSizeLimit}
                    />
                )}
                {activeTab === "record" && (
                    <TabRecordAudio
                        onAudioReady={(audioFile) => handleAudioReady(audioFile, "record")}
                    />
                )}
            </div>

            {/* 统一的音频预览组件 */}
            <AudioPreview
                projAudio={projAudio}
                onReset={resetAudio}
            />
        </div>
    );
};

/**
 * Tab1: 文本转语音组件
 */
const TabInputText = ({ onAudioReady }) => {
    const [text, setText] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
    const maxChars = 1000;
    const { selectedVoice } = useProjectStore();
    const { setUserCredits } = useUserStore();

    const handleGenerate = async () => {
        if (!text.trim()) {
            alert('Please enter some text');
            return;
        }

        setIsGenerating(true);
        try {
            // 调用 TTS API，传递选中的音色 ID
            const response = await fetch('/api/elevenlabs-tts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: text.trim(),
                    voiceId: selectedVoice?.voice_id || "21m00Tcm4TlvDq8ikWAM",
                    voiceName: selectedVoice?.name || "Rachel"
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            // 从响应头获取新的积分信息（如果存在）
            const newCredits = response.headers.get('X-User-Credits');
            if (newCredits !== null) {
                setUserCredits(parseInt(newCredits));
            }

            // 将响应转换为 Blob
            const audioBlob = await response.blob();

            // 创建 File 对象
            const audioFile = new File(
                [audioBlob],
                `tts-${Date.now()}.mp3`,
                { type: 'audio/mpeg' }
            );

            // 传递给父组件处理
            onAudioReady(audioFile);
        } catch (error) {
            console.error('TTS generation failed:', error);
            alert(`Failed to generate audio: ${error.message || 'Please try again.'}`);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleVoiceConfirm = (voice) => {
        // Modal 内部已经更新了 store，这里只需要关闭 modal
        setIsVoiceModalOpen(false);
    };

    const getVoiceDisplayText = () => {
        if (!selectedVoice) return "Select Voice";
        const labels = selectedVoice.labels || {};
        const gender = labels.gender || "unknown";
        const language = labels.language || "unknown";
        // 显示格式：名称 (ID: xxx, 语言: xxx, 性别: xxx)
        // return `${selectedVoice.name} ,${language}, ${gender}`;
        return (
            <div className='flex items-center gap-2'>
                <div className='p-3 bg-base-100 rounded-full'><Speech size={20} /></div>
                <div className='flex flex-col items-start'>
                    <span className="text-sm font-bold">{selectedVoice.name}</span>
                    <span className="text-sm text-base-content/75">🇬🇧🇺🇸 {gender.charAt(0).toUpperCase() + gender.slice(1)}</span>
                </div>
            </div>
        )
    };

    return (
        <div className="space-y-2">
            <div className='relative'>
                {/* 文本输入框 */}
                <textarea
                    className="textarea textarea-bordered w-full"
                    placeholder="Enter text to convert to speech (max 1000 characters)"
                    value={text}
                    onChange={(e) => {
                        if (e.target.value.length <= maxChars) {
                            setText(e.target.value);
                        }
                    }}
                    rows={4}
                    maxLength={maxChars}
                />
                <div className="text-right text-xs text-base-content/60 mt-1 absolute bottom-2 right-3">
                    {text.length} / {maxChars} characters
                </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2 justify-between items-end ">
                {/* 音色选择按钮 */}
                <fieldset className="fieldset bg-base-200 border-base-300 rounded-box w-full md:w-1/2 border p-3">
                    <legend className="fieldset-legend">Voice selected</legend>
                    <div className='flex items-center gap-2 justify-between'>
                        <span className="text-sm">{getVoiceDisplayText()}</span>
                        <div className='btn btn-sm btn-ghost' onClick={() => setIsVoiceModalOpen(true)}>Change <ChevronRight size={16} /></div>
                    </div>
                </fieldset>

                {/* 生成音频按钮 */}
                <div className='flex flex-col items-end gap-2 mt-4 md:mt-0'>
                    <div className='text-xs text-right'>
                        <div><span className='text-base-content/65'>Free/Base Plan:</span> <span className=''>2 Credits</span> /generation</div>
                        <div><span className='text-base-content/65'>Premium/Pro Plan:</span> <span className='font-bold'>Unlimited Usage</span></div>
                    </div>
                    <button
                        className="btn btn-primary py-2 flex-1 mt-1"
                        onClick={handleGenerate}
                        disabled={isGenerating || !text.trim()}
                    >
                        {isGenerating ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Generating...
                            </>
                        ) : (
                            'Generate Audio'
                        )}
                    </button>
                </div>
            </div>

            {/* 音色选择 Modal */}
            <VoiceSelectModal
                isOpen={isVoiceModalOpen}
                onClose={() => setIsVoiceModalOpen(false)}
                onConfirm={handleVoiceConfirm}
            />
        </div>
    );
};

/**
 * Tab2: 上传音频组件（保留原有逻辑）
 */
const TabUploadAudio = ({ onAudioReady, getMaxAudioSizeLimit }) => {
    const uploadRef = useRef(null);
    const fileInput = useRef(null);
    const { openPricingModal } = useStore();

    const triggerFileInput = () => {
        fileInput.current.click();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        uploadRef.current.style.background = 'oklch(var(--b3))';
        event.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = () => {
        uploadRef.current.style.background = 'none';
    };

    const handleFileDrop = (event) => {
        event.preventDefault();
        handleFiles(event);
    };

    const handleFiles = (event) => {
        const files = event.target.files || event.dataTransfer.files;
        const audioFile = files[0];

        if (!audioFile) return;

        // 验证文件格式
        if (audioFile.type !== 'audio/mp3' && audioFile.type !== 'audio/wav' && audioFile.type !== 'audio/mpeg') {
            alert('Audio format must be MP3 or WAV');
            resetFileInput();
            return;
        }

        // 检查音频文件大小权限
        const maxAudioSize = getMaxAudioSizeLimit();
        if (maxAudioSize !== null && audioFile.size > maxAudioSize) {
            const maxSizeMB = (maxAudioSize / (1024 * 1024)).toFixed(0);
            openPricingModal({
                title: "Audio Too Large",
                description: `Your current plan limits audio uploads to ${maxSizeMB}MB. Upgrade your subscription to upload larger audio files and create longer lip-sync videos.`
            });
            resetFileInput();
            return;
        }

        onAudioReady(audioFile);
        resetFileInput();
    };

    const resetFileInput = () => {
        fileInput.current.value = null;
    };

    return (
        <div
            className="file-upload-container bg-base-200 border border-base-content/40 border-dashed rounded-lg p-8 cursor-pointer"
            ref={uploadRef}
            onClick={triggerFileInput}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleFileDrop}
        >
            <input
                id="inputFileAudio"
                type="file"
                ref={fileInput}
                onChange={handleFiles}
                accept="audio/mp3,audio/wav,audio/mpeg"
                style={{ display: 'none' }}
            />

            <div className="flex flex-col items-center">
                <div className="mb-4 flex justify-center">
                    <img src="https://rs.magicface.co/indiesites/lipsync-show/app/File-Audio.webp" alt="audio-example" className="w-32" />
                </div>
                <div className="text-white">
                    <span className="underline">Click</span>
                    <span> or drag original audio here</span>
                </div>
                <div className="mt-2 text-xs text-warning/90">
                    mp3, wav file in 5 min, less than 20MB
                </div>
            </div>
        </div>
    );
};

/**
 * 音频波形图组件 - 实时显示麦克风音量
 */
const AudioWaveform = ({ audioStream, isActive }) => {
    const [volumes, setVolumes] = useState(new Array(20).fill(0));
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const audioContextRef = useRef(null);

    useEffect(() => {
        if (!audioStream || !isActive) {
            // 重置波形图
            setVolumes(new Array(20).fill(0));
            return;
        }

        // 创建 AudioContext 和 AnalyserNode
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(audioStream);

        analyser.fftSize = 64; // 设置 FFT 大小，影响频率分析的精度
        analyser.smoothingTimeConstant = 0.8; // 平滑系数，使波形更平滑
        microphone.connect(analyser);

        analyserRef.current = analyser;
        audioContextRef.current = audioContext;

        // 实时更新波形数据
        const updateWaveform = () => {
            if (!isActive || !analyserRef.current) return;

            const analyser = analyserRef.current;
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(dataArray);

            // 将频率数据转换为柱状图高度（20个柱子）
            const barCount = 20;
            const step = Math.floor(dataArray.length / barCount);
            const newVolumes = [];

            for (let i = 0; i < barCount; i++) {
                let sum = 0;
                for (let j = 0; j < step; j++) {
                    sum += dataArray[i * step + j] || 0;
                }
                // 归一化到 0-100，并添加一些最小高度使波形更明显
                const avg = sum / step;
                const normalized = Math.min(100, (avg / 255) * 100);
                newVolumes.push(Math.max(5, normalized)); // 最小高度为 5%
            }

            setVolumes(newVolumes);
            animationFrameRef.current = requestAnimationFrame(updateWaveform);
        };

        updateWaveform();

        // 清理函数
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
        };
    }, [audioStream, isActive]);

    return (
        <div className="flex items-end justify-center gap-1 h-20 w-full max-w-md px-4">
            {volumes.map((volume, index) => (
                <div
                    key={index}
                    className="bg-error rounded-t transition-all duration-75 ease-out"
                    style={{
                        width: '8px',
                        height: `${volume}%`,
                        minHeight: '4px',
                    }}
                />
            ))}
        </div>
    );
};

/**
 * Tab3: 录音组件
 */
const TabRecordAudio = ({ onAudioReady }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioStreamRef = useRef(null);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioStreamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioFile = new File([audioBlob], `record-${Date.now()}.webm`, { type: 'audio/webm' });
                onAudioReady(audioFile);

                // 停止所有音频轨道
                if (audioStreamRef.current) {
                    audioStreamRef.current.getTracks().forEach(track => track.stop());
                    audioStreamRef.current = null;
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            // 开始计时
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Failed to access microphone. Please check your permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);

            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="flex flex-col items-center justify-center py-8">
            {!isRecording ? (
                <button
                    className="btn btn-primary btn-lg"
                    onClick={startRecording}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    Start Recording
                </button>
            ) : (
                <div className="flex flex-col items-center gap-4">
                    <div className="text-2xl font-bold text-error mb-2">
                        <AudioWaveform audioStream={audioStreamRef.current} isActive={isRecording} />
                    </div>
                    <div className="text-lg">Recording: {formatTime(recordingTime)}</div>
                    <button
                        className="btn btn-error btn-lg"
                        onClick={stopRecording}
                    >
                        Stop Recording
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * 统一的音频预览组件
 */
const AudioPreview = ({ projAudio, onReset }) => {
    if (!projAudio.source) {
        return null;
    }

    return (
        <div className="bg-accent/10 border border-accent/40 rounded-lg p-4 mt-8">
            <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                    <div className="text-sm text-accent font-bold mb-1">Audio Preview</div>
                    <div className="text-base font-medium">{projAudio.name}</div>
                    <div className="text-xs text-base-content/60 mt-1">
                        Duration: {parseFloat(projAudio.duration).toFixed(2)}s
                    </div>
                </div>
                <button
                    className="btn btn-sm btn-ghost text-error"
                    onClick={onReset}
                >
                    Delete <Trash size={16} />
                </button>
            </div>
            <div className="flex justify-center">
                <audio
                    id="slot_audio"
                    src={projAudio.source}
                    className="w-full max-w-md h-12"
                    controls
                />
            </div>
        </div>
    );
};

export default Step2Audio;
